import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from '@hookform/error-message';
import addressValidation from '../Validation/AddressValidaton';
import useMakeRequest from '../utils/ApiHelper';
import { ApiTypes } from '../constants/Constants';
import { ApiConstants } from '../constants/ApiConstants';
import FrontLoader from '../component/layout/FrontLoader';
import { useNavigate } from 'react-router-dom';
import LocationInput from '../component/address/LocationInput';
import { LanguageConstants } from '../constants/LanguageConstants';
import { buildSortedShippingDisplayRows } from '../utils/shippingDisplay';
import { attachAddressLine2ForApi } from '../utils/addressPayload';

interface FormInputs {
    street_line: string;
    appartment: string;
    state:string;
    city:string;
    zip_code:string;
    loaction_type:string;
    instructions:string
  };

export type NewAddressWizardHandle = {
  submitWizard: () => void;
};

const NewAddress = forwardRef<NewAddressWizardHandle, any>(function NewAddress(prop, ref) {
    const deliveryPanelInline = Boolean(prop?.deliveryPanelInline);
    const wizardInlineRequested = Boolean(prop?.wizardInline);
    const wizardInline = wizardInlineRequested || deliveryPanelInline;
    const hideShipMethodAndPrimaryInForm =
      Boolean(prop?.hideShipMethodAndPrimaryInForm) || deliveryPanelInline;
    const showCancelInForm = deliveryPanelInline || Boolean(prop?.showCancelInForm);
    /** Manual/bulk wizard passes `showSubmitInForm` so inline “+ New Address” shows Save (not only footer Next). */
    const showSubmitInForm =
      prop?.showSubmitInForm === true ||
      !wizardInlineRequested ||
      deliveryPanelInline;
    const idPre = deliveryPanelInline ? 'addr-vq-del' : wizardInlineRequested ? 'addr-wiz' : 'addr-mod';

    const addressWizardSuccessHandled = useRef(false)
    const addressAdd  = useMakeRequest()
    const addressUpdate = useMakeRequest()
    const shippingListRequest = useMakeRequest()
    const navigate = useNavigate()
    /** `ship_method_trans.ref_id` as string, or "" if none selected */
    const [shippingMethod, setShippingMethod] = useState('')
    const [setAsPrimary, setSetAsPrimary] = useState(false)
    const [billingSameAsShipping, setBillingSameAsShipping] = useState(false)
    const [country, setCountry] = useState('')

    const shippingRows = useMemo(() => {
        return buildSortedShippingDisplayRows(shippingListRequest?.data?.data).map(({ refId, label }) => ({
            refId,
            name: label,
        }))
    }, [shippingListRequest?.data?.data])

    const { register, watch, setValue, formState: { errors }, handleSubmit } = useForm<FormInputs>({
        mode: 'onChange',
        resolver: yupResolver(addressValidation),
      });
      useEffect(() => {
        if (hideShipMethodAndPrimaryInForm) return;
        shippingListRequest.makeRequest({ url: ApiConstants?.ShippingList, toastStatus: false });
      }, [hideShipMethodAndPrimaryInForm]);

      useEffect(() => {
        const v = prop?.initialLoactionType;
        if (v === "1" || v === "2" || v === "3") {
          setValue("loaction_type", v);
        }
      }, [prop?.initialLoactionType, setValue]);

      const onSubmit = useCallback((payload: FormInputs) => {
        if (addressAdd?.loading) return;
        const shipping_id =
            shippingMethod != null && String(shippingMethod).trim() !== ''
                ? Number(shippingMethod)
                : null
        addressAdd.makeRequest({
            payload: attachAddressLine2ForApi({
                ...payload,
                country,
                shipping_id: Number.isFinite(shipping_id as number) && (shipping_id as number) > 0 ? shipping_id : null,
                is_primary: setAsPrimary ? 1 : 0,
                billing_same_as_shipping: billingSameAsShipping ? 1 : 0,
            }),
            type: ApiTypes.POST,
            url: ApiConstants?.AddressAdd,
            toastStatus: true,
        })
     }, [addressAdd, shippingMethod, setAsPrimary, billingSameAsShipping, country])

      useImperativeHandle(
        ref,
        () => ({
          submitWizard: () => {
            if (addressAdd?.loading) return;
            void handleSubmit(onSubmit)();
          },
        }),
        [addressAdd?.loading, handleSubmit, onSubmit],
      )

    useEffect(() => {
        if (!addressAdd?.status) return;

        // Quote wizard: stay on page, no full reload (preserves step + form state).
        if (typeof prop?.onAddressCreated === "function") {
            if (addressWizardSuccessHandled.current) return;
            addressWizardSuccessHandled.current = true;
            const raw = addressAdd?.data?.data;
            const newId = raw?.id ?? raw;
            if (newId != null && newId !== "") {
                prop.onAddressCreated(newId, addressAdd?.data?.data);
            }
            addressAdd.reset();
            if (!wizardInlineRequested && !deliveryPanelInline) {
              prop.handleClose();
            }
            return;
        }

        if (prop?.data) {
            const payload = {
                address_id: addressAdd?.data?.data?.id,
                quotation_id: prop?.data?.quotation_id,
            };
            addressUpdate.makeRequest({ payload, url: ApiConstants?.QuotesAddressUpdate, toastStatus: true });
            setTimeout(() => {
                prop.handleClose();
                navigate("/my-quotation");
                window.location.reload();
            }, 2000);
        } else {
            prop.handleClose();
            window.location.reload();
        }
    }, [addressAdd.status]);

    const handleLocationSuccess = (result: any) => {
        setValue('street_line', result?.formattedAddress || '')
        setValue('city', result?.city || '')
        setValue('state', result?.state || '')
        if (result?.zip_code) setValue('zip_code', result.zip_code)
        setCountry(result?.country || '')
    }

  const showWizardBootstrapLoader =
    wizardInline && !showSubmitInForm && Boolean(shippingListRequest?.loading);

  /** Modal / inline-with-save: block only while submitting. First-address wizard: block until shipping list ready. */
  const showBlockingLoader = addressAdd?.loading || showWizardBootstrapLoader;

  useEffect(() => {
    if (!wizardInlineRequested || typeof prop?.onInlineBlockingLoadChange !== 'function') return;
    prop.onInlineBlockingLoadChange(showBlockingLoader);
  }, [showBlockingLoader, wizardInlineRequested, prop?.onInlineBlockingLoadChange]);

  useEffect(() => {
    if (!wizardInlineRequested || typeof prop?.onInlineBlockingLoadChange !== 'function') return;
    return () => {
      prop.onInlineBlockingLoadChange(false);
    };
  }, [wizardInlineRequested, prop?.onInlineBlockingLoadChange]);

  return (
      <>
      {showBlockingLoader && !deliveryPanelInline && !wizardInlineRequested ? <FrontLoader/> : null }
                <div className={wizardInline ? 'address-form-inline text-start' : 'address-form-card text-start'}>
                    {!wizardInline ? (
                    <div className="address-form-card__header">
                        <span className="address-book-badge">Address Book</span>
                        <button onClick={()=>prop.handleClose()} type="button" className="address-form-card__close" data-bs-dismiss="modal" aria-label="Close">
                            <i className="icon-close-square" />
                        </button>
                    </div>
                    ) : null}
                    <form onSubmit={handleSubmit(onSubmit)} className="address-form-layout">
                        <div className="address-form-section">
                            <h5 className="address-form-section__title">{LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION}</h5>
                            <div className="address-form-grid">
                                <div className="address-form-field">
                                    <label className="address-form-label">{LanguageConstants?.ADDRESS_LINE_1}</label>
                                    <LocationInput
                                        id={`${idPre}-street`}
                                        className="form-control address-form-input"
                                        onSuccess={handleLocationSuccess}
                                        value={watch("street_line")}
                                        placeholder={wizardInline ? LanguageConstants?.MANUAL_QUOTE_PLACEHOLDER_ADDRESS_LINE : LanguageConstants?.ENTER_STREE_LINE}
                                        onChange={(e) => {
                                            setValue('street_line', e?.target?.value)
                                        }}
                                    />
                                    <ErrorMessage
                                        errors={errors}
                                        name="street_line"
                                        render={({ message }) => <p className="text-danger small mb-0 mt-1">{message}</p>}
                                    />
                                </div>
                                <div className="address-form-field">
                                    <label className="address-form-label">{LanguageConstants?.ADDRESS_LINE_2}</label>
                                    <input type="text" className="form-control address-form-input" placeholder={wizardInline ? LanguageConstants?.MANUAL_QUOTE_PLACEHOLDER_ADDRESS_LINE : LanguageConstants?.ENTER_APARTMENT} {...register('appartment')} />
                                    <ErrorMessage
                                        errors={errors}
                                        name="appartment"
                                        render={({ message }) => <p className="text-danger small mb-0 mt-1">{message}</p>}
                                    />
                                </div>
                                <div className="address-form-field">
                                    <label className="address-form-label">{LanguageConstants?.MANUAL_QUOTE_INSTRUCTIONS}</label>
                                    <input type="text" className="form-control address-form-input" placeholder={wizardInline ? LanguageConstants?.MANUAL_QUOTE_PLACEHOLDER_INSTRUCTIONS : LanguageConstants?.ENTER_INSTRUCTIONS} {...register('instructions')} />
                                    <ErrorMessage
                                        errors={errors}
                                        name="instructions"
                                        render={({ message }) => <p className="text-danger small mb-0 mt-1">{message}</p>}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* <div className="address-form-divider"></div> */}

                        <div className="address-form-section">
                            <h5 className="address-form-section__title">{LanguageConstants?.MANUAL_QUOTE_RESIDENCE}</h5>
                            <p className="address-form-section__subtitle">{LanguageConstants?.MANUAL_QUOTE_SELECT_RESIDENCE}</p>
                            <div className="d-flex gap-4 flex-wrap">
                                <div className="form-check">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        id={`${idPre}-res-1`}
                                        value="1"
                                        {...register('loaction_type')}
                                    />
                                    <label className="form-check-label" htmlFor={`${idPre}-res-1`}>
                                        {LanguageConstants?.RESIDENTIAL}
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        id={`${idPre}-res-2`}
                                        value="2"
                                        {...register('loaction_type')}
                                    />
                                    <label className="form-check-label" htmlFor={`${idPre}-res-2`}>
                                        {LanguageConstants?.LABORAL}
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        id={`${idPre}-res-3`}
                                        value="3"
                                        {...register('loaction_type')}
                                    />
                                    <label className="form-check-label" htmlFor={`${idPre}-res-3`}>
                                        {LanguageConstants?.INDUSTRIAL}
                                    </label>
                                </div>
                            </div>
                            <ErrorMessage
                                errors={errors}
                                name="loaction_type"
                                render={({ message }) => <p className="text-danger small mb-0 mt-2">{message}</p>}
                            />
                        </div>

                        {!hideShipMethodAndPrimaryInForm ? (
                        <>
                        <div className="address-form-divider"></div>

                            <div className="address-form-section">
                              <h5 className="address-form-section__title">{LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION}</h5>
                              <p className="address-form-section__subtitle">{LanguageConstants?.SHIPPINGMETHOD}</p>
                              <div className="d-flex flex-wrap gap-4">
                                  {shippingRows.map((item) => (
                                      <div className="form-check" key={item.refId}>
                                          <input
                                              type="radio"
                                              className="form-check-input"
                                              id={`${idPre}-ship-${item.refId}`}
                                              name={`${idPre}-shipping_method`}
                                              value={item.refId}
                                              checked={shippingMethod === item.refId}
                                              onChange={(e) => setShippingMethod(e.target.value)}
                                          />
                                          <label className="form-check-label" htmlFor={`${idPre}-ship-${item.refId}`}>
                                              {item.name}
                                          </label>
                                      </div>
                                  ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="address-form-divider my-0"></div>
                        )}

                        <div className={`address-form-checks${wizardInline ? ' address-form-checks--wizard' : ''}`}>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`${idPre}-billing-same`}
                                    checked={billingSameAsShipping}
                                    onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`${idPre}-billing-same`}>
                                    {LanguageConstants?.MANUAL_QUOTE_BILLING_SAME}
                                </label>
                            </div>
                            {!hideShipMethodAndPrimaryInForm && !wizardInline ? (
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`${idPre}-primary`}
                                    checked={setAsPrimary}
                                    onChange={(e) => setSetAsPrimary(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`${idPre}-primary`}>
                                    {LanguageConstants?.BULK_SET_PRIMARY_ADDRESS}
                                </label>
                            </div>
                            ) : null}
                        </div>

                        {showSubmitInForm ? (
                        <div
                            className={`address-form-actions${wizardInlineRequested ? ' address-form-actions--wizard' : ''}`}
                        >
                            {showCancelInForm ? (
                                <button
                                    type="button"
                                    className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill"
                                    onClick={() => prop?.handleClose?.()}
                                >
                                    {LanguageConstants?.CANCEL ?? 'Cancel'}
                                </button>
                            ) : null}
                            <button
                                type="submit"
                                className={
                                    wizardInlineRequested
                                        ? 'manual-quote-btn com-btn rounded-pill'
                                        : 'manual-quote-btn com-btn rounded-pill'
                                }
                                disabled={addressAdd?.loading}
                            >
                                {prop?.submitLabel ?? LanguageConstants?.SAVE}
                            </button>
                        </div>
                        ) : null}
                    </form>
                </div>
    </>
  )
})

export default NewAddress